import React from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { StatsCard } from '@/components/dashboard/StatsCard';
import { UsageChart } from '@/components/dashboard/UsageChart';
import { QuotaWidget } from '@/components/dashboard/QuotaWidget';
import { DashboardServices } from '@/services/DashboardServices';
import { useSession } from '@/contexts/SessionContext';
import {
  FiTrendingUp,
  FiFileText,
  FiUsers,
  FiDollarSign,
  FiCheckCircle,
  FiClock,
} from 'react-icons/fi';

const PageHeader = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const RoleBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  background: ${theme.colors.primary}20;
  color: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  margin-bottom: ${theme.spacing.md};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const PageSubtitle = styled.p`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text}80;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const RecentActivity = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
`;

const ActivityTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
  transition: background ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
  }
`;

const ActivityIcon = styled.div<{ type: 'success' | 'warning' | 'info' }>`
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    switch (props.type) {
      case 'success':
        return theme.colors.success + '20';
      case 'warning':
        return theme.colors.warning + '20';
      default:
        return theme.colors.primary + '20';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'success':
        return theme.colors.success;
      case 'warning':
        return theme.colors.warning;
      default:
        return theme.colors.primary;
    }
  }};
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityText = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const ActivityTime = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing['2xl']};
  color: ${theme.colors.text}60;
`;

const ErrorState = styled.div`
  background: ${theme.colors.error}10;
  color: ${theme.colors.error};
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  border-left: 4px solid ${theme.colors.error};
`;

export const OrganizationDashboard: React.FC = () => {
  const { loggedUser: user } = useSession();

  const { data: dashboard, isLoading, error} = useQuery({
    queryKey: ['org-dashboard', (user as any)?.organization_id],
    queryFn: async () => {
      const result = await DashboardServices.getOrganizationDashboard((user as any)?.organization_id!);
      return result.data;
    },
    enabled: !!(user as any)?.organization_id,
  });

  if (isLoading) {
    return (
      <DashboardLayout>
        <LoadingState>Chargement du tableau de bord...</LoadingState>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <ErrorState>
          Erreur lors du chargement du tableau de bord. Veuillez réessayer.
        </ErrorState>
      </DashboardLayout>
    );
  }

  // Mock data for demonstration
  const mockDashboard = {
    usage: {
      scorings: { used: 85, limit: 100 },
      storage: { used_gb: 3.2, limit_gb: 5 },
      users: { active: 8, limit: 10 },
    },
    stats: {
      total_applications: 245,
      pending_applications: 12,
      total_scorings: 85,
      revenue_this_month: 2450,
    },
    recent_activity: [
      { date: '1 Jan', scorings: 12, applications: 20 },
      { date: '5 Jan', scorings: 19, applications: 32 },
      { date: '10 Jan', scorings: 15, applications: 28 },
      { date: '15 Jan', scorings: 25, applications: 40 },
      { date: '20 Jan', scorings: 22, applications: 35 },
      { date: '25 Jan', scorings: 30, applications: 45 },
      { date: '30 Jan', scorings: 28, applications: 42 },
    ],
  };

  const displayData = dashboard || mockDashboard;

  return (
    <DashboardLayout>
      <PageHeader>
        <RoleBadge>👨‍💼 Interface Administrateur / Organisation</RoleBadge>
        <PageTitle>Tableau de bord - Administration</PageTitle>
        <PageSubtitle>
          Gérez votre organisation, suivez les statistiques globales et supervisez toutes les activités
        </PageSubtitle>
      </PageHeader>

      <StatsGrid>
        <StatsCard
          title="Candidatures totales"
          value={displayData.stats?.total_applications || 245}
          icon={<FiFileText />}
          color={theme.colors.primary}
          trend={{ value: 12, isPositive: true }}
          subtitle="Ce mois-ci"
        />
        <StatsCard
          title="En attente"
          value={displayData.stats?.pending_applications || 12}
          icon={<FiClock />}
          color={theme.colors.warning}
          subtitle="À traiter"
        />
        <StatsCard
          title="Scorings effectués"
          value={displayData.stats?.total_scorings || 85}
          icon={<FiTrendingUp />}
          color={theme.colors.accent}
          trend={{ value: 8, isPositive: true }}
          subtitle="Ce mois-ci"
        />
        <StatsCard
          title="Utilisateurs actifs"
          value={displayData.usage?.users?.active || 8}
          icon={<FiUsers />}
          color={theme.colors.success}
          subtitle={`Sur ${displayData.usage?.users?.limit || 10} max`}
        />
      </StatsGrid>

      <ChartsGrid>
        <UsageChart data={displayData.recent_activity} />
        <QuotaWidget quotas={displayData.usage} />
      </ChartsGrid>

      <RecentActivity>
        <ActivityTitle>Activité récente</ActivityTitle>
        <ActivityList>
          <ActivityItem>
            <ActivityIcon type="success">
              <FiCheckCircle />
            </ActivityIcon>
            <ActivityContent>
              <ActivityText>
                Candidature #1234 approuvée par la commission
              </ActivityText>
              <ActivityTime>Il y a 5 minutes</ActivityTime>
            </ActivityContent>
          </ActivityItem>

          <ActivityItem>
            <ActivityIcon type="info">
              <FiTrendingUp />
            </ActivityIcon>
            <ActivityContent>
              <ActivityText>
                Nouveau scoring effectué pour Jean Dupont (Score: 85/100)
              </ActivityText>
              <ActivityTime>Il y a 15 minutes</ActivityTime>
            </ActivityContent>
          </ActivityItem>

          <ActivityItem>
            <ActivityIcon type="info">
              <FiFileText />
            </ActivityIcon>
            <ActivityContent>
              <ActivityText>
                Nouvelle candidature soumise par Marie Martin
              </ActivityText>
              <ActivityTime>Il y a 1 heure</ActivityTime>
            </ActivityContent>
          </ActivityItem>

          <ActivityItem>
            <ActivityIcon type="warning">
              <FiUsers />
            </ActivityIcon>
            <ActivityContent>
              <ActivityText>
                Nouvel utilisateur ajouté: Pierre Dubois (Validateur)
              </ActivityText>
              <ActivityTime>Il y a 2 heures</ActivityTime>
            </ActivityContent>
          </ActivityItem>

          <ActivityItem>
            <ActivityIcon type="success">
              <FiDollarSign />
            </ActivityIcon>
            <ActivityContent>
              <ActivityText>
                Paiement reçu: 299 FCFA (Plan Professional)
              </ActivityText>
              <ActivityTime>Il y a 3 heures</ActivityTime>
            </ActivityContent>
          </ActivityItem>
        </ActivityList>
      </RecentActivity>
    </DashboardLayout>
  );
};
